import { area as turfArea } from "@turf/area";
import booleanPointInPolygon from "@turf/boolean-point-in-polygon";
import booleanPointOnLine from "@turf/boolean-point-on-line";
import { booleanWithin } from "@turf/boolean-within";
import {
  lineString,
  lineString as turfLine,
  point as turfPoint,
  polygon as turfPolygon,
} from "@turf/helpers";
import type { FeatureCollection, Position } from "geojson";

import { AccessibleColorThemes, type AllColorThemes, ColorThemes } from "@/features/app/constants";
import IMDFFeature from "@/libs/imdf/feature";
import IMDFTheme from "@/libs/imdf/theme";
import type { FeatureByLevelHashMap } from "@/libs/imdf/types";
import type { Building, Floor, LatLng } from "../models";
import { geometriesToExtrude, matchFeatures } from "./utils";

export const IMDF_WORKER_ID = "imdf-worker";

class IMDF {
  name: string;
  featuresByLevel: FeatureByLevelHashMap = {};
  commonFeatures: Array<IMDFFeature> = [];
  private _isValidIMDF = false;
  private _isValidGeoJSON = false;
  theme: IMDFTheme = new IMDFTheme();
  lastChangeTimestamp: number;
  uniqueFeatureId = 0;
  themes: Record<string, IMDFTheme> = {};

  private MAXDIST_METERS_CENTROIDS_FORTRANSFER = 15; //meters

  constructor(geojson: any | null = null, name?: string) {
    this.name = name ?? "";
    this.update();

    if (geojson && Object.keys(geojson).length !== 0) {
      this.createFromBuildingGeoJSON(geojson);
    }
  }

  static buildFromFeatures(features: IMDFFeature[]) {
    const imdf = new IMDF();
    imdf.setFeatures(features);
    imdf.featuresByLevel = imdf.splitFeaturesByLevel(features);
    imdf.commonFeatures = imdf.getCommonFeatures(features);

    return imdf;
  }

  public createFromBuildingGeoJSON(geojson: any) {
    let features = this.geoJSONToFeatures(geojson);

    this.validateIMDF(features);

    features = this.prepareIMDF(features);
    this.featuresByLevel = this.splitFeaturesByLevel(features);
    this.commonFeatures = this.getCommonFeatures(features);
  }

  private geoJSONToFeatures(geojson: any): IMDFFeature[] {
    return geojson
      ? geojson.features?.map(
          (feature: any) =>
            new IMDFFeature({
              feature_type: feature.feature_type,
              geometry: feature.geometry,
              id: feature.id,
              properties: feature.properties,
              type: feature.type,
            }),
        )
      : [];
  }

  private prepareIMDF(
    features: Array<IMDFFeature>,
    level_id?: string,
  ): Array<IMDFFeature> {
    const setFeatureType = (feature: IMDFFeature) => {
      if (!feature.properties.feature_type) {
        feature.properties["feature_type"] = feature.feature_type;
      }
      return feature;
    };

    const setGeometryFromDisplayPoint = (feature: IMDFFeature) => {
      if (!feature.geometry && feature.properties.display_point) {
        feature.geometry = feature.properties.display_point;
      }
      return feature;
    };

    const hasGeometry = (feature: IMDFFeature) => {
      return Boolean(feature.geometry);
    };

    const ensureHasGeometry = (feature: IMDFFeature) => {
      if (!hasGeometry(feature) || Object.keys(feature.geometry).length === 0) {
        feature["geometry"] = { coordinates: [], type: "Polygon" };
      }
      return feature;
    };

    const assignUniqueId = (feature: IMDFFeature) => {
      if (feature.feature_type === "level") {
        return feature;
      }
      feature["id"] = String(this.uniqueFeatureId++);
      return feature;
    };

    const setLevelId = (feature: IMDFFeature, level_id: string) => {
      if (!feature.properties.level_id) {
        feature.properties["level_id"] = level_id;
      }
      return feature;
    };

    return features.map((feature) => {
      feature = setGeometryFromDisplayPoint(feature);
      feature = setFeatureType(feature);
      feature = assignUniqueId(feature);
      if (level_id) {
        feature = setLevelId(feature, level_id);
      }
      feature = ensureHasGeometry(feature);
      return feature;
    }, []);
  }

  private splitFeaturesByLevel(
    features: Array<IMDFFeature>,
  ): FeatureByLevelHashMap {
    const fByLevel: FeatureByLevelHashMap = {};

    const s: { [id: string]: number } = {};
    features.forEach((f: IMDFFeature) => {
      if (f.feature_type === "level") {
        s[f.id] = f.properties.ordinal;
      }
    });

    if (Object.keys(s).length === 0) return fByLevel;

    features.forEach((f: IMDFFeature) => {
      if (f.feature_type === "level") return;

      if (f.properties.level_id) {
        const ordinal = s[f.properties.level_id];
        if (!fByLevel[ordinal]) {
          fByLevel[ordinal] = [];
        }
        fByLevel[ordinal].push(f);
      }
    });
    return fByLevel;
  }

  private getCommonFeatures(features: Array<IMDFFeature>): Array<IMDFFeature> {
    return features.filter((f) => !f.properties.level_id);
  }

  // Map for worker
  static fromMap(data: IMDF) {
    const imdf = new IMDF({}, data.name);
    imdf.featuresByLevel = Object.entries(data.featuresByLevel).reduce(
      (acc, [level, features]) => {
        acc[level] = features.map((feature) => new IMDFFeature({ ...feature }));
        return acc;
      },
      {} as Record<string, IMDFFeature[]>,
    );
    imdf.theme = IMDFTheme.fromMap(data.theme);
    imdf.lastChangeTimestamp = data.lastChangeTimestamp;
    imdf.uniqueFeatureId = data.uniqueFeatureId;
    Object.keys(data.themes).forEach((theme) => {
      imdf.themes[theme] = IMDFTheme.fromMap(data.themes[theme]);
    });
    imdf.commonFeatures = data.commonFeatures;
    return imdf;
  }

  //Downloads full IMDF of the venue. Contains level features
  public getGeoJSON(): FeatureCollection {
    const geojson = {
      features: [
        ...this.commonFeatures.map((feature) => feature.getGeoJSONFeature()),
        ...Object.values(this.featuresByLevel)
          .flat()
          .map((feature) => feature.getGeoJSONFeature()),
      ],
      type: "FeatureCollection" as const,
    };
    return geojson;
  }

  //Downloads full IMDF of the venue. Does not contain level features
  public getGeoJSONByFloorLevel(level: number): FeatureCollection {
    return {
      features: [
        ...this.featuresByLevel[level]
          .flat()
          .map((feature) => feature.getGeoJSONFeature()),
      ],
      type: "FeatureCollection",
    };
  }

  public loadTheme(key: AllColorThemes, theme: IMDFTheme) {
    this.themes = {
      ...this.themes,
      [key]: theme,
    };
  }

  public changeTheme(key: AllColorThemes) {
    if (!this.themes[key]) {
      console.debug(`Could not find imdf theme with key`, key);
      return;
    }
    this.update();
    this.theme = this.themes[key];

    // Prioritize color theme for accessible themes
    if (Object.values(AccessibleColorThemes).includes(key)) {
      this.theme.setOverrideFeaturesProps(true);
    } else {
      this.theme.setOverrideFeaturesProps(false);
    }
  }

  /**
   * @deprecated use `loadTheme` with its theme key, and `changeTheme`, instead of `useTheme`
   */
  public setTheme(theme: IMDFTheme) {
    this.loadTheme(ColorThemes.light, theme);
    this.changeTheme(ColorThemes.light);
  }

  public getLevelOrdinals(): number[] {
    return Object.keys(this.featuresByLevel).map(Number);
  }

  public getFeatureById(id: string): IMDFFeature | undefined {
    for (const [, features] of Object.entries(this.featuresByLevel)) {
      // Find the index of the feature in the current level's array
      const index = features.findIndex((f) => f.id === id);

      if (index !== -1) {
        return features[index];
      }
    }
    return undefined;
  }

  public getFeaturesByFloorLevel(
    floorLevel: number,
    visibleOnly = false,
  ): Array<IMDFFeature> {
    const features = this.featuresByLevel[floorLevel];

    visibleOnly &&
      features.filter((f: IMDFFeature) => {
        if ("show" in f.properties && f.properties.show === false) return false;

        return true;
      });

    if (features !== undefined) return features;
    else return [];
  }

  public getFeaturesBeforeId(
    id: string,
    levelId: number,
  ): IMDFFeature[] | undefined {
    const levelMap = this.getLevelMap();
    const level = levelMap[levelId];
    const features = this.getFeaturesByFloorLevel(level);
    const index = features.findIndex((f) => f.id === id);
    if (index !== -1) {
      // Return all features from the start up to and including the found feature
      return features.slice(0, index + 1);
    }
    return undefined;
  }

  public getFeatures(): Array<IMDFFeature> {
    return Object.values(this.featuresByLevel).flat();
  }

  /**
   * Updates a single feature in the `featuresByLevel` map.
   * If the feature is found in any level, it replaces the existing entry and triggers an update.
   *
   * @param feature - The `IMDFFeature` object to update.
   */
  public updateFeature(feature: IMDFFeature) {
    for (const [, features] of Object.entries(this.featuresByLevel)) {
      // Find the index of the feature in the current level's array
      const index = features.findIndex((f) => f.id === feature.id);

      if (index !== -1) {
        // Feature found, update it
        features[index] = feature;
        this.update();
        break; // Stop the search if feature is found and updated
      }
    }
  }

  /**
   * Updates multiple features in the `featuresByLevel` map.
   * If any feature exists in the current levels, it replaces them with the new ones.
   *
   * @param newFeatures - An array of `IMDFFeature` objects to update.
   */
  public updateFeatures(newFeatures: IMDFFeature[]) {
    let somethingChanged = false;

    newFeatures.forEach((newFeature) => {
      for (const features of Object.values(this.featuresByLevel)) {
        // Find the index of the feature in the current level's array
        const index = features.findIndex((f) => f.id === newFeature.id);

        if (index !== -1) {
          // Feature found, update it
          features[index] = newFeature;
          somethingChanged = true;
          break; // Stop the search if feature is found and updated
        }
      }
    });

    if (somethingChanged) {
      this.update();
    }
  }

  /**
   * Replaces the entire `featuresByLevel` map with new features.
   *
   * @param newFeatures - An array of `IMDFFeature` objects to set.
   */
  public setFeatures(newFeatures: IMDFFeature[]) {
    const levelMap = this.getLevelMap();

    // Update feature map with new features
    this.featuresByLevel = newFeatures.reduce<Record<number, IMDFFeature[]>>(
      (acc, feature) => {
        const level = levelMap[feature.properties.level_id];
        if (level !== undefined) {
          (acc[level] ||= []).push(feature);
        }
        return acc;
      },
      {},
    );

    this.update();
  }

  public removeFeatureById(id: string) {
    for (const [, features] of Object.entries(this.featuresByLevel)) {
      // Find the index of the feature in the current level's array
      const index = features.findIndex((f) => f.id === id);

      if (index !== -1) {
        // Feature found, update it
        delete features[index];
      }
    }
  }

  public createFromFloorsGeoJSON(geojsons: Record<number, IMDFFeature[]>) {
    for (const key in geojsons) {
      let features = this.geoJSONToFeatures(geojsons[key]);
      const levelFeature = this.createLevelFeature(Number(key));
      this.commonFeatures.push(levelFeature);
      this.validateIMDF(features);

      features = this.prepareIMDF(features, levelFeature.id);

      this.featuresByLevel[key] = features;
    }
  }

  /**
   * Returns a map of `level_id` to level ordinal, with `level_id` being the ID of the
   * level feature, not the ID of the Situm Floor.
   */
  private getLevelMap() {
    return this.commonFeatures
      .filter((feature) => feature.feature_type === "level")
      .reduce<Record<string, number>>((acc, feature) => {
        if (feature.properties?.ordinal !== undefined) {
          acc[feature.id] = feature.properties.ordinal;
        }
        return acc;
      }, {});
  }

  /**
   * Returns true if the IMDF has any feature with `feature_type` set to `level`.
   */
  public hasLevelFeatures() {
    return this.commonFeatures.some(
      (feature) => feature.feature_type === "level",
    );
  }

  /**
   * Creates a new level feature with the given ordinal.
   * @param ordinal - The ordinal of the level.
   * @returns The new level feature.
   */
  private createLevelFeature(ordinal: number) {
    return new IMDFFeature({
      feature_type: "level",
      geometry: { coordinates: [], type: "Polygon" },
      id: String(this.uniqueFeatureId++),
      properties: { category: undefined, ordinal: ordinal },
      type: "Feature",
    });
  }

  /**
   * Returns the ID of the level feature with the given ordinal.
   * @param ordinal - The ordinal of the level.
   * @returns The ID of the level feature.
   */
  public getLevelIdFromOrdinal(ordinal: number) {
    return this.commonFeatures.find(
      (feature) =>
        feature.feature_type === "level" &&
        feature.properties.ordinal === ordinal,
    )?.id;
  }

  /**
   * Creates the base level features for the IMDF.
   * @param building - The building to create the base level features for.
   */
  public createLevelFeatures(building: Building) {
    building.floors?.forEach((floor) => {
      const levelFeature = this.createLevelFeature(floor.level);
      this.commonFeatures.push(levelFeature);
    });
    this.update();
  }

  /**
   * Generates a new feature with the given geometry and properties.
   *
   * @param geometry - The geometry of the feature.
   * @param properties - The properties of the feature.
   * @param floorId - The ID of the floor.
   *
   * @returns The new feature.
   */
  public generateNewFeature(
    geometry: IMDFFeature["geometry"],
    properties: IMDFFeature["properties"],
    floorId: Floor["id"],
  ) {
    return new IMDFFeature({
      geometry: geometry,
      id: String(this.uniqueFeatureId++),
      properties: {
        ...properties,
        level_id: this.getLevelIdFromOrdinal(floorId),
      },
      type: "Feature",
    });
  }

  public findFeatureByCoordinate(floorLevel: number, lat: number, lng: number) {
    const point = turfPoint([lng, lat]);
    let features = this.featuresByLevel[floorLevel]?.filter(
      (f: IMDFFeature) => {
        if (f.properties.show === false) return false;

        // Check if the feature is a Polygon
        if (
          f.geometry.type === "Polygon" &&
          f.geometry.coordinates.length > 0
        ) {
          const polygon = turfPolygon(f.geometry.coordinates);
          return booleanPointInPolygon(point, polygon);
        } else if (f.geometry.type === "MultiPolygon") {
          for (const coordinates of f.geometry.coordinates) {
            if (booleanPointInPolygon(point, turfPolygon(coordinates)))
              return true;
          }
          return false;
        } else if (f.geometry.type === "LineString") {
          return booleanPointOnLine(point, lineString(f.geometry.coordinates), {
            epsilon: 0.0000001,
          });
        }
        return false;
      },
    );

    // Calculate area and sort if needed
    features = features
      .map((f: IMDFFeature) => {
        if (f.geometry.type === "Polygon") {
          f.properties.area = turfArea(turfPolygon(f.geometry.coordinates));
        } else if (f.geometry.type === "MultiPolygon") {
          let area = 0;
          for (const coordinates of f.geometry.coordinates) {
            area += turfArea(turfPolygon(coordinates));
          }
          f.properties.area = area;
        }
        return f;
      })
      .sort((a, b) => a.properties.area - b.properties.area);

    // Return the list of features
    return features && features.length > 0 ? features[0] : null;
  }

  public findFeaturesByPolygon(
    floorLevel: number,
    polygonCoords: Array<LatLng>,
  ) {
    if (polygonCoords.length < 3) return [];
    // Convert LatLng to GeoJSON Polygon and ensure it's closed
    const polygonCoordinates = [
      ...polygonCoords.map((coord) => [coord.lng, coord.lat]),
      [polygonCoords[0]?.lng, polygonCoords[0]?.lat], // Close the polygon
    ];

    //Create the polygon based on which we will search
    const searchPolygon = turfPolygon([polygonCoordinates]);

    // Filter features within the polygon on the given floor
    const features = this.featuresByLevel[floorLevel].filter(
      (f: IMDFFeature) => {
        if (f.properties.show === false) return false;

        // Is polygon within searchPolygon?
        if (
          f.geometry.type === "Polygon" &&
          f.geometry.coordinates.length > 0
        ) {
          return booleanWithin(
            turfPolygon(f.geometry.coordinates),
            searchPolygon,
          );
        } else if (f.geometry.type === "MultiPolygon") {
          // Is any polygon of the multipolygon within searchPolygon?
          return f.geometry.coordinates.some((coordinates: Position[][]) => {
            return booleanWithin(turfPolygon(coordinates), searchPolygon);
          });
        } else if (f.geometry.type === "LineString") {
          return booleanWithin(turfLine(f.geometry.coordinates), searchPolygon);
        }
        return false;
      },
    );

    // Return the list of features enclosed within the polygon
    return features;
  }

  public isValidIMDF(): boolean {
    return this._isValidIMDF;
  }
  public isValidGeoJSON(): boolean {
    return this._isValidGeoJSON;
  }

  //TODO: Remove
  validateIMDF(features: IMDFFeature[]) {
    if (!features) {
      console.debug("IMDF validation error >> No features found", this.name);
      return false;
    }

    if (features.length === 0) {
      console.debug("IMDF validation error >> No features found", this.name);
      return false;
    }

    let invalidTypeCount = 0;
    let invalidIdCount = 0;
    let invalidRingCount = 0;

    features.forEach((feature: any) => {
      const invalidType = feature.type === undefined || feature.type === null;
      const invalidId = feature.id === undefined || feature.id === null;

      if (invalidType) {
        invalidTypeCount++;
      }

      if (invalidId) {
        invalidIdCount++;
      }
      if (geometriesToExtrude.includes(feature?.geometry?.type)) {
        feature.geometry.coordinates.forEach((ring: any) => {
          if (ring.length < 4) {
            invalidRingCount++;
          }
        });
      }
    });

    if (invalidTypeCount > 0) {
      console.debug(
        `IMDF validation warning >>`,
        invalidTypeCount,
        `features have an undefined or null feature type`,
        this.name,
      );
    }

    if (invalidIdCount > 0) {
      console.debug(
        `IMDF validation warning >>`,
        invalidIdCount,
        `features have an undefined or null ID`,
        this.name,
      );
    }

    if (invalidRingCount > 0) {
      console.debug(
        `IMDF validation warning >> `,
        invalidRingCount,
        `features have an invalid ring`,
        this.name,
      );
    }
  }

  public transferProperties(fromIMDF: IMDF) {
    //Returns a list of pairs [thisFeatureId, fromIMDFFeatureID], each pair stating
    // that the best match for a feature id of "this" is another feature id from "fromIMDF"
    const matches = matchFeatures(
      this,
      fromIMDF,
      this.MAXDIST_METERS_CENTROIDS_FORTRANSFER,
    );

    //Transfer properties
    for (const match of matches) {
      const toFeatureId = match[0];
      const fromFeatureId = match[1];

      const toFeature = this.getFeatureById(toFeatureId);
      const fromFeature = fromIMDF.getFeatureById(fromFeatureId);

      if (toFeature && fromFeature) {
        if ("feature_type" in fromFeature) {
          toFeature.feature_type = fromFeature.feature_type;
        }

        if ("category" in fromFeature.properties) {
          toFeature.properties.category = fromFeature.properties.category;
        }

        if ("extrusionHeight" in fromFeature.properties) {
          toFeature.properties.extrusionHeight =
            fromFeature.properties.extrusionHeight;
        }

        if ("fillColor" in fromFeature.properties) {
          toFeature.properties.fillColor = fromFeature.properties.fillColor;
        }

        if ("fillOpacity" in fromFeature.properties) {
          toFeature.properties.fillOpacity = fromFeature.properties.fillOpacity;
        }

        if ("margin" in fromFeature.properties) {
          toFeature.properties.margin = fromFeature.properties.margin;
        }

        this.updateFeature(toFeature);
      }
    }
  }

  public update() {
    this.lastChangeTimestamp = Date.now();
  }

  public clone(): IMDF {
    const clonedIMDF = new IMDF({}, this.name);
    clonedIMDF.name = this.name;
    clonedIMDF._isValidIMDF = this._isValidIMDF;
    clonedIMDF._isValidGeoJSON = this._isValidGeoJSON;
    clonedIMDF.lastChangeTimestamp = Date.now();
    clonedIMDF.uniqueFeatureId = this.uniqueFeatureId;
    clonedIMDF.theme = this.theme.clone();
    clonedIMDF.featuresByLevel = Object.entries(this.featuresByLevel).reduce(
      (acc, [level, features]) => {
        acc[level] = features.map((feature) => new IMDFFeature({ ...feature }));
        return acc;
      },
      {} as Record<string, IMDFFeature[]>,
    );
    clonedIMDF.commonFeatures = this.commonFeatures.map(
      (feature) => new IMDFFeature({ ...feature }),
    );
    clonedIMDF.themes = {};
    Object.keys(this.themes).forEach((themeKey) => {
      clonedIMDF.themes[themeKey] = this.themes[themeKey].clone();
    });

    return clonedIMDF;
  }
}

export default IMDF;
